import React, { createContext, useContext, useState, useCallback, useMemo, useRef } from 'react';
import { ToastProvider, ToastViewport, Toast, ToastTitle, ToastDescription, ToastClose, ToastIcon } from './toast';

const ToasterContext = createContext(null);

let _idSeq = 0;
const nextId = () => `t_${Date.now()}_${++_idSeq}`;

export function useToast() {
  const ctx = useContext(ToasterContext);
  if (!ctx) {
    // fallback no-op si el componente no está dentro del Toaster
    return {
      toast: (opts) => console.warn('Toaster no disponible', opts),
      success: (title, description) => console.warn('Toaster no disponible', title, description),
      error: (title, description) => console.warn('Toaster no disponible', title, description),
      info: (title, description) => console.warn('Toaster no disponible', title, description),
      dismiss: () => {},
    };
  }
  return ctx;
}

export function Toaster({ children }) {
  const [toasts, setToasts] = useState([]);
  const timers = useRef({});

  const dismiss = useCallback((id) => {
    setToasts((cur) => cur.filter((t) => t.id !== id));
    if (timers.current[id]) {
      clearTimeout(timers.current[id]);
      delete timers.current[id];
    }
  }, []);

  const toast = useCallback(
    ({ title, description, variant = 'default', duration = 4000 } = {}) => {
      const id = nextId();
      setToasts((cur) => [...cur, { id, title, description, variant, open: true }]);
      timers.current[id] = setTimeout(() => dismiss(id), duration);
      return id;
    },
    [dismiss]
  );

  const dismissById = useCallback((id) => dismiss(id), [dismiss]);

  // IMPORTANTE: memoizar el value para que NO cambie en cada render.
  // Si se recrea, cualquier `useCallback(..., [toast])` o `useEffect(..., [toast])`
  // en los consumidores se invalida y dispara re-fetches, loadings, etc.
  const value = useMemo(
    () => ({
      toast,
      success: (title, description) => toast({ title, description, variant: 'success' }),
      error: (title, description) => toast({ title, description, variant: 'destructive' }),
      info: (title, description) => toast({ title, description, variant: 'default' }),
      dismiss: dismissById,
    }),
    [toast, dismissById],
  );

  return (
    <ToasterContext.Provider value={value}>
      <ToastProvider swipeDirection="right">
        {children}
        {toasts.map((t) => (
          <Toast
            key={t.id}
            variant={t.variant}
            open={t.open}
            onOpenChange={(open) => {
              if (!open) dismiss(t.id);
            }}
            duration={4000}
          >
            <div className="flex items-start gap-3">
              <ToastIcon variant={t.variant} />
              <div className="flex-1 space-y-0.5">
                {t.title && <ToastTitle>{t.title}</ToastTitle>}
                {t.description && <ToastDescription>{t.description}</ToastDescription>}
              </div>
            </div>
            <ToastClose />
          </Toast>
        ))}
        <ToastViewport />
      </ToastProvider>
    </ToasterContext.Provider>
  );
}
